import { createSlice } from "@reduxjs/toolkit";

export const querySlice = createSlice({
  name: "query",
  initialState: {
    queryConfig: null,
    queryContext: null,

    outcomeNodes: [],
    selectedOutcomeNodes: [],
    relevanceNodes: [],
    selectedRelevanceNodes: [],

    requiredNodes: [],

    isSmallytics: false,

    errorDialog: {
      type: null,
      message: null,
      is_display: false,
    },
    errorBar: {
      message: null,
      is_display: false,
    },
    dialog: {
      is_display: false,
    },
    report: {
      is_display: false,
    },

    isLoading: false,
  },
  reducers: {
    setQueryConfig: (state, action) => {
      state.queryConfig = action.payload;
    },
    setQueryContext: (state, action) => {
      state.queryContext = action.payload;
    },

    setOutcomeNodes: (state, action) => {
      state.outcomeNodes = action.payload;
    },
    setSelectedOutcomeNodes: (state, action) => {
      state.selectedOutcomeNodes = action.payload;
    },
    setRelevanceNodes: (state, action) => {
      state.relevanceNodes = action.payload;
    },
    setSelectedRelevanceNodes: (state, action) => {
      state.selectedRelevanceNodes = action.payload;
    },
    setRequiredNodes: (state, action) => {
      state.requiredNodes = action.payload;
    },

    setIsSmallytics: (state, action) => {
      state.isSmallytics = action.payload;
    },
    switchIsSmallytics: (state) => {
      state.isSmallytics = !state.isSmallytics;
    },

    setErrorDialog: (state, action) => {
      state.errorDialog = action.payload;
    },
    setErrorBar: (state, action) => {
      state.errorBar = action.payload;
    },
    switchDialog: (state) => {
      state.dialog = { is_display: !state.dialog.is_display };
    },
    setReport: (state, action) => {
      state.report = action.payload;
    },

    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
  },
});

export const {
  setQueryConfig,
  setQueryContext,

  setOutcomeNodes,
  setSelectedOutcomeNodes,
  setRelevanceNodes,
  setSelectedRelevanceNodes,
  setRequiredNodes,

  setIsSmallytics,
  switchIsSmallytics,

  setErrorDialog,
  setErrorBar,
  switchDialog,
  setReport,

  setIsLoading,
} = querySlice.actions;

// --- Selectors ---
export const selectQueryConfig = (state) => state.query.queryConfig;

export const selectQueryContext = (state) => state.query.queryContext;

export const selectSubjectGroups = (state) => {
  const group = state.query.queryContext?.subject_groups?.[0];
  if (!group?.attrs) return null;
  const noFilter = group.attrs.find((attr) => attr.key === "no_filter");
  if (noFilter?.value) return null;
  return group.attrs
    .filter((attr) => attr.key !== "no_filter" && attr.value)
    .map((attr) => attr.key);
};

export const selectAgeAttrs = (state) =>
  state.query.queryContext?.subject_attrs
    ?.find((item) => item.key === "age")
    ?.attrs?.filter((attr) => attr.value)
    .map((attr) => attr.key) ?? [];

export const selectGenderAttrs = (state) =>
  state.query.queryContext?.subject_attrs
    ?.find((item) => item.key === "gender")
    ?.attrs?.filter((attr) => attr.value)
    .map((attr) => attr.key) ?? [];

export const selectTimeGrain = (state) =>
  state.query.queryContext?.time_grain?.find((item) => item.value)?.key ??
  "one_day";

export const selectTimeRange = (state) =>
  state.query.queryContext?.time_range ?? { start: null, end: null };

export const selectIsNoApps = (state) =>
  state.query.queryContext?.is_noapps ?? false;

export const selectOutcomeNodes = (state) => state.query.outcomeNodes;
export const selectSelectedOutcomeNode = (state) =>
  state.query.selectedOutcomeNodes;
export const selectRelevanceNodes = (state) => state.query.relevanceNodes;
export const selectSelectedRelevanceNode = (state) =>
  state.query.selectedRelevanceNodes;
export const selectRequiredNodes = (state) => state.query.requiredNodes;

export const selectIsSmallytics = (state) => state.query.isSmallytics;

export const selectErrorDialog = (state) => state.query.errorDialog;
export const selectErrorBar = (state) => state.query.errorBar;
export const selectDialog = (state) => state.query.dialog;
export const selectReport = (state) => state.query.report;

export const selectIsLoading = (state) => state.query.isLoading;

// --- Extraction ---
export const extractSubjectGroups = (queryContext) => {
  const group = queryContext?.subject_groups?.[0];
  if (!group?.attrs) return null;
  const noFilter = group.attrs.find((attr) => attr.key === "no_filter");
  if (noFilter?.value) return null;
  return group.attrs
    .filter((attr) => attr.key !== "no_filter" && attr.value)
    .map((attr) => ({
      group_id: attr.id,
      group_name: attr.key,
    }));
};

export const extractAgeAttrs = (queryContext) =>
  queryContext?.subject_attrs
    ?.find((item) => item.key === "age")
    ?.attrs?.filter((attr) => attr.value)
    .map((attr) => attr.id) ?? [];

export const extractGenderAttrs = (queryContext) => {
  const genderAttrs =
    queryContext?.subject_attrs?.find((item) => item.key === "gender")?.attrs ??
    [];
  const genderMap = { 0: "male", 1: "female", 2: "other" };
  const result = { male: false, female: false, other: false };
  genderAttrs.forEach((attr) => {
    const label = genderMap[attr.key];
    if (label) result[label] = !!attr.value;
  });
  return result;
};

export const extractTimeGrain = (queryContext) => {
  const key =
    queryContext?.time_grain?.find((item) => item.value)?.key ?? "one_day";

  const table = (() => {
    switch (key) {
      case "one_day":
        return "datawarehouse_day_preprocessed";
      case "one_week":
        return "datawarehouse_week_preprocessed";
      case "one_month":
        return "datawarehouse_month_preprocessed";
      default:
        return "datawarehouse_day_preprocessed";
    }
  })();

  return { key, table };
};

export const extractTimeRange = (queryContext) =>
  queryContext?.time_range ?? { start: null, end: null };

export const extractIsNoApps = (queryContext) =>
  queryContext?.is_noapps ?? false;

// --- Inserter ---
export const adaptQueryConfig = (data) => {
  const columnsObject = {};
  const bodyObject = {};
  const symptomsObject = {};
  const columnsLabelObject = {};
  const columnsENLabelObject = {};
  const bodyLabelObject = {};
  const symptomsLabelObject = {};

  for (let item of data.column_properties) {
    columnsObject[item.value] = item;
    columnsLabelObject[item.name] = item;
    columnsENLabelObject[item.name_en] = item;
  }
  for (let item of data.symptom_body_name) {
    bodyObject[item.value] = item;
    bodyLabelObject[item.name] = item;
  }
  for (let item of data.symptom_name) {
    symptomsObject[item.value] = item;
    symptomsLabelObject[item.name] = item;
  }

  return {
    columns: columnsObject,
    body: bodyObject,
    symptoms: symptomsObject,
    columnsByLabel: columnsLabelObject,
    bodyByLabel: bodyLabelObject,
    symptomsByLabel: symptomsLabelObject,
    columnsByENLabel: columnsENLabelObject,
    column_properties: data.column_properties,
  };
};

// --- Reducer ---
export default querySlice.reducer;
